import React from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import type { ProjectMeta } from '../../data/types';
import PlatformBadges from '../project/PlatformBadges';

const CardLink = styled(motion.create(Link))`
  min-width: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.bgLight};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  box-shadow: ${({ theme }) => theme.shadows.card};
  text-decoration: none;
  transform: translateZ(0);
  transition: border-color 220ms ease, box-shadow 220ms ease, transform 220ms ease;

  &:hover {
    border-color: rgba(99, 102, 241, 0.3);
    box-shadow: ${({ theme }) => theme.shadows.cardHover};
    transform: translateY(-6px);
  }
`;

const Media = styled.div<{
  $accent: string;
  $presentation: 'cover' | 'icon' | 'portrait';
  $backgroundImage?: string;
}>`
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: ${({ $accent, $backgroundImage, $presentation, theme }) => (
    $presentation === 'portrait' && $backgroundImage
      ? `linear-gradient(rgba(15, 11, 26, 0.72), rgba(15, 11, 26, 0.82)), url("${$backgroundImage}") center / cover`
      : `linear-gradient(135deg, ${$accent}22, ${theme.colors.bgDark})`
  )};

  img {
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    object-fit: ${({ $presentation }) => $presentation === 'cover' ? 'cover' : 'contain'};
    padding: ${({ $presentation }) => $presentation === 'icon' ? '10%' : $presentation === 'portrait' ? '3%' : '0'};
    transition: transform 350ms ease;
  }

  ${CardLink}:hover & img {
    transform: scale(1.025);
  }
`;

const Body = styled.div`
  padding: 24px;
`;

const Eyebrow = styled.p`
  margin: 0 0 8px;
  color: ${({ theme }) => theme.colors.accentStrong};
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const Title = styled.h3`
  margin: 0 0 8px;
  font-size: 1.5rem;
  line-height: 1.2;
  letter-spacing: -0.03em;
`;

const Hook = styled.p`
  min-height: 3em;
  margin: 0 0 18px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Footer = styled.div`
  min-height: 62px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
`;

const Outcome = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.82rem;
  font-weight: 750;
`;

const HomeProjectCard: React.FC<{ project: ProjectMeta; index: number }> = ({ project, index }) => {
  const reduceMotion = useReducedMotion();
  const outcome = project.outcomes?.[0];
  const mediaPresentation = project.cardMedia?.type === 'image' ? project.cardMedia.presentation ?? 'cover' : 'cover';

  return (
    <CardLink
      to={`/projects/${project.slug}`}
      data-testid="project-card"
      aria-label={`View ${project.title} project`}
      initial={reduceMotion ? false : { opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: reduceMotion ? 0 : 0.38, delay: reduceMotion ? 0 : index * 0.06 }}
    >
      <Media
        $accent={project.accentColor ?? '#6366f1'}
        $presentation={mediaPresentation}
        $backgroundImage={mediaPresentation === 'portrait' ? project.cardMedia?.src : undefined}
        data-testid={`project-card-${project.slug}`}
      >
        {project.cardMedia ? <img src={project.cardMedia.src} alt={project.cardMedia.alt} loading={index > 2 ? 'lazy' : 'eager'} /> : null}
      </Media>
      <Body>
        <Eyebrow>{project.eyebrow}</Eyebrow>
        <Title>{project.title}</Title>
        <Hook>{project.hook}</Hook>
        <Footer>
          <PlatformBadges platforms={project.platforms} />
          {outcome ? <Outcome>{outcome.label} {outcome.value}</Outcome> : <Outcome>{project.statusLabel ?? project.status.replaceAll('-', ' ')}</Outcome>}
        </Footer>
      </Body>
    </CardLink>
  );
};

export default HomeProjectCard;
